import React from 'react';
import { appPortalName, getHostPrefix } from './config';
import { Q_HTTP_TIMEOUT } from './utils';

// /////////////////////////////////////////////////////////////////////////////////////////////////
const CODE_STRINGS = new Map([
  [200, 'ok'],
  [201, 'created'],
  [400, 'invalid client request'],
  [401, 'unauthorized'],
  [402, 'payment required'],
  [403, 'forbidden'],
  [404, 'not found'],
  [500, 'internal server error'],
  [501, 'not implemented'],
  [503, 'service unavailable'],
]);

let _refreshTokenPromise = null;

// /////////////////////////////////////////////////////////////////////////////////////////////////
function fixedEncodeURIComponent(str) {
  return encodeURIComponent(str).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function getQueryString(params) {
  if (!params) return '';
  return Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${fixedEncodeURIComponent(k)}=${fixedEncodeURIComponent(v)}`)
    .join('&');
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
async function fetchWithTimeout(url, options = {}) {
  const { timeout = Q_HTTP_TIMEOUT, ...rest } = options;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(url, { ...rest, signal: controller.signal });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw { status: 408, statusText: 'Request timed out.', name: 'AbortError' };
    }
    throw err;
  } finally {
    clearTimeout(id);
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
class HttpReq {
  constructor() {
    this.headers = new Headers();
    this.headers.append('Accept', 'application/json');
    this.headers.append('Content-Type', 'application/json');
    this.headers.append('X-Portal', appPortalName);
    this._extraHeaderProvider = null;
    this._updateUserTokenAsync = null;
    this.onUnauthorized = null;
    this.getStore = null;
    this.accessToken = null;
  }

  setUpdateUserTokenAsync(fn) {
    this._updateUserTokenAsync = fn;
  }

  setExtraHeaderProvider(fn) {
    this._extraHeaderProvider = fn;
  }

  setUnauthorizedHandler(fn) {
    this.onUnauthorized = fn;
  }

  setGetStore(fn) {
    this.getStore = fn;
  }

  setAccessToken(token) {
    this.accessToken = token;
    if (token) {
      console.log('[HttpReq] Access token set:', token);
    } else {
      console.log('[HttpReq] Access token cleared');
    }
  }

  // Build a Headers instance with defaults and optional bearer token.
  _createHeaders(accept = 'application/json', contentType = 'application/json') {
    let globalStore = this.getStore?.();
    if (!globalStore) {
      console.warn('[HttpReq] No store available, using default headers only');
    }
    let state = globalStore?.getState?.();
    let bearerToken = this.accessToken || state?.user?.token;
    const headers = new Headers();
    headers.append('Accept', accept);
    headers.append('Content-Type', contentType);
    headers.append('X-Portal', appPortalName);
    if (this._extraHeaderProvider) {
      const extra = this._extraHeaderProvider();
      // console.warn('extra headers', extra);
      // Object.entries(extra).forEach(([k, v]) => {
      //   if (v != null) headers.set(k, v);
      // });
    }
    if (bearerToken) headers.set('Authorization', 'Bearer ' + bearerToken);
    return headers;
  }

  // Standardize error handling across methods.
  _handleError(err, url, data, errFunc) {
    console.warn('HttpReq _handleError', err, url, data);
    if (err && err.status === 401) {
      if (typeof this.onUnauthorized === 'function') {
        if (!url?.startsWith('/auth/local')) {
          try {
            this.onUnauthorized(err, url, data);
          } catch (err) {
            console.error('Error in onUnauthorized handler:', err);
          }
          if (err.status && !err.statusText) err.statusText = CODE_STRINGS.get(err.status);
          return Promise.reject(err); // still reject so caller sees the failure if they care
        }
      }
    }

    if (errFunc && errFunc !== this.fetchCatchErr) {
      return errFunc(err, url, data);
    }
    return this.fetchCatchErr(err, url, data);
  }

  // Internal method that performs token refresh WITHOUT triggering normal 401 logic
  async _refreshAccessToken(dispatch, tokenInfo) {
    try {
      const url = getHostPrefix() + '/auth/refresh';
      // Use fetch directly so we don't call _requestJson again!
      const res = await fetchWithTimeout(url, {
        method: 'POST',
        headers: this._createHeaders(),
        body: JSON.stringify({ tokenInfo }),
        credentials: 'include',
        timeout: 15_000,
      });
      if (res.status !== 200) {
        console.warn('Token refresh failed', res.status, res.statusText);
        return null;
      }
      const replyJson = await res.json();

      const token = replyJson?.token;
      if (!token) {
        console.warn('Token refresh failed: no token in response');
        return null;
      }

      this.setAccessToken(token); // Make the fresh token available to *immediate* retries.

      // Fire-and-forget store update to avoid deadlock with refresh.
      if (this._updateUserTokenAsync && typeof dispatch === 'function') {
        Promise.resolve().then(() => {
          dispatch(this._updateUserTokenAsync(token))
            .then((action) => {
              if (action?.meta?.requestStatus !== 'fulfilled') {
                console.warn('Token refresh action not fulfilled', action);
              }
            })
            .catch((e) => console.error('Token refresh action error', e));
        });
      }
      // Resolve now so _refreshTokenDeduped settles and callers can retry.
      return token;
    } catch (err) {
      console.error('Token refresh exception', err);
      return null;
    }
  }

  async _refreshTokenDeduped(dispatch, tokenInfo) {
    if (!_refreshTokenPromise) {
      _refreshTokenPromise = this._refreshAccessToken(dispatch, tokenInfo).finally(() => {
        _refreshTokenPromise = null;
      });
    }
    return _refreshTokenPromise;
  }

  async _fetchWith401Retry(method, fullUrl, init, aUrl, aData, errFunc, timeout, parseResponse) {
    let res;
    try {
      if (timeout > 0) {
        res = await fetchWithTimeout(fullUrl, { method, ...init, timeout });
      } else {
        res = await fetch(fullUrl, { method, ...init });
      }
    } catch (err) {
      return this._handleError(err, aUrl, aData, errFunc);
    }

    if (res.status === 401) {
      let globalStore = this.getStore?.();
      if (globalStore) {
        const userState = globalStore.getState().user;
        const tokenInfo = userState?.tokenInfo;
        let token = await this._refreshTokenDeduped(globalStore.dispatch, tokenInfo);
        if (token) {
          this.setAccessToken(token);
          const newHeaders = new Headers(init.headers);
          newHeaders.set('Authorization', 'Bearer ' + token);
          try {
            res = await fetchWithTimeout(fullUrl, { method, ...init, headers: newHeaders, timeout: 20_000 });
          } catch (err) {
            console.error('HttpReq _fetchWith401Retry', err);
            return this._handleError(err, aUrl, aData, errFunc);
          }
        }
        // If no token (refresh failed) or retried call still 401, bail out now.
        if (!token || res.status === 401) {
          console.warn('HttpReq _fetchWith401Retry', 'No token available', token, res);
          return this._handleError({ status: 401 }, aUrl, aData, errFunc);
        }
      }
    }
    return parseResponse(res, aUrl, aData, errFunc);
  }

  // Generic JSON request helper: fetch → status check → parse → log → errors
  async _requestJson(method, aUrl, init, aData, errFunc = this.fetchCatchErr) {
    const fullUrl = getHostPrefix() + aUrl;
    return this._fetchWith401Retry(method, fullUrl, init, aUrl, aData, errFunc, 0, async (res, aUrl, aData, errFunc) => {
      if (res.status !== 200) {
        // Try to read an error body, but don't let it hang the UI
        let payload = { status: res.status, statusText: res.statusText };
        try {
          const bodyPromise = res.text(); // text is more tolerant than json()
          const text = await Promise.race([bodyPromise, new Promise((_, rej) => setTimeout(() => rej(new Error('err-body-timeout')), 200))]);
          try {
            payload = JSON.parse(text);
          } catch {
            payload.message = text;
          }
        } catch (_) {
          /* swallow – keep default payload */
        }
        return this._handleError(payload, aUrl, aData, errFunc);
      }
      const json = await res.json();
      console.groupCollapsed('fetch', method, aUrl, aData);
      console.log(json);
      console.groupEnd();
      return json;
    });
  }

  // Generic text request helper
  async _requestText(method, aUrl, init, aData, errFunc = this.fetchCatchErr) {
    const fullUrl = getHostPrefix() + aUrl;
    return this._fetchWith401Retry(method, fullUrl, init, aUrl, aData, errFunc, 0, async (res, aUrl, aData, errFunc) => {
      if (res.status !== 200) {
        let payload;
        try {
          payload = await res.text();
        } catch {
          payload = { status: res.status, statusText: res.statusText };
        }
        return this._handleError(payload, aUrl, aData, errFunc);
      }
      const text = await res.text();
      console.groupCollapsed('fetch', method, aUrl, aData);
      console.log(text);
      console.groupEnd();
      return text;
    });
  }

  // Generic blob request helper
  async _requestBlob(method, aUrl, init, aData, timeout = 0) {
    const errFunc = this.fetchCatchErr;
    const fullUrl = getHostPrefix() + aUrl;
    return this._fetchWith401Retry(method, fullUrl, init, aUrl, aData, errFunc, timeout, async (res, aUrl, aData, errFunc) => {
      if (res.status !== 200) {
        let payload;
        try {
          payload = await res.json();
        } catch {
          payload = { status: res.status, statusText: res.statusText };
        }
        return this._handleError(payload, aUrl, aData, errFunc);
      }
      const blob = await res.blob();
      console.groupCollapsed('fetch', method, aUrl, aData);
      console.log(blob);
      console.groupEnd();
      return blob;
    });
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchPostJson(aUrl, aData) {
    const headers = this._createHeaders();
    return this._requestJson('POST', aUrl, { headers, credentials: 'include', body: JSON.stringify(aData) }, aData);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchGetJson(aUrl, aData) {
    const headers = this._createHeaders();
    const qs = getQueryString(aData);
    return this._requestJson('GET', `${aUrl}${qs ? `?${qs}` : ''}`, { headers, credentials: 'include' }, aData);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchPatchJson(aUrl, aData) {
    const headers = this._createHeaders();
    return this._requestJson('PATCH', aUrl, { headers, credentials: 'include', body: JSON.stringify(aData) }, aData);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchDeleteJson(aUrl, aData) {
    const headers = this._createHeaders();
    const init = { headers, credentials: 'include' };
    if (aData) init.body = JSON.stringify(aData);
    return this._requestJson('DELETE', aUrl, init, aData);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchPostJsonAcceptAny(aUrl, aData) {
    const headers = this._createHeaders('text/plain');
    return this._requestText('POST', aUrl, { headers, credentials: 'include', body: JSON.stringify(aData) }, aData);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchGetJpg(aUrl, aData, timeout = Q_HTTP_TIMEOUT) {
    const headers = this._createHeaders('image/jpeg');
    const qs = getQueryString(aData);
    return this._requestBlob('GET', `${aUrl}${qs ? `?${qs}` : ''}`, { headers, credentials: 'include' }, aData, timeout);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchGetFile(aUrl, aData, timeout = Q_HTTP_TIMEOUT) {
    const headers = this._createHeaders('application/octet-stream');
    const qs = getQueryString(aData);
    return this._requestBlob('GET', `${aUrl}${qs ? `?${qs}` : ''}`, { headers, credentials: 'include' }, aData, timeout);
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchGetCsv(aUrl, aData) {
    const headers = this._createHeaders();
    const query = getQueryString(aData);
    const url = `${aUrl}${query ? `?${query}` : ''}`;
    const blob = await this._requestBlob('GET', url, { headers, credentials: 'include' }, aData);

    // existing CSV download logic...
    console.groupCollapsed('fetch', 'GET', aUrl, aData);
    console.log(aData, 'Blob response');
    const urlObj = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlObj;

    let filename = aData.entityName;
    if (aData.entityName === 'Maintenance') {
      const labels = ['Maintenance Schedule', 'Maintenance Upcoming', 'Maintenance History'];
      filename = labels[aData.tabIndex] || 'Maintenance';
    }
    else if(aData.entityName === 'Dvir') {
      const labels = ['Dvirs', 'Defects', 'PreTripDvirSignoff'];
      filename = labels[aData.tabIndex] || 'Dvir';
    }
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(urlObj), 10);
    console.groupEnd();
    return blob;
  }

  // /////////////////////////////////////////////////////////////////////////////////////////////////
  async fetchCatchErr(err, aUrl, aData) {
    console.groupCollapsed('fetch', aUrl, aData);
    console.error(err);
    console.groupEnd();

    if (err.status && !err.statusText) {
      err.statusText = CODE_STRINGS.get(err.status);
      throw err;
    }
    throw err;
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const g_HttpReq = new HttpReq();
